// ============================================================
// Fee Receipt Modal — Dual-Copy Side-by-Side (2 on 1 Page)
// ============================================================

import { useState, useRef } from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, Box, Typography, IconButton } from '@mui/material';
import { X, Printer, Download, MessageSquare, Mail } from 'lucide-react';
import { tokens } from '@/theme/tokens';
import dayjs from 'dayjs';
import html2canvas from 'html2canvas';
import { jsPDF } from 'jspdf';
import toast from 'react-hot-toast';
import { APP_NAME } from '@/constants/app';

function numberToWordsINR(num) {
  if (!num || num === 0) return 'ZERO';
  const ones = ['', 'ONE', 'TWO', 'THREE', 'FOUR', 'FIVE', 'SIX', 'SEVEN', 'EIGHT', 'NINE', 'TEN',
    'ELEVEN', 'TWELVE', 'THIRTEEN', 'FOURTEEN', 'FIFTEEN', 'SIXTEEN', 'SEVENTEEN', 'EIGHTEEN', 'NINETEEN'];
  const tens = ['', '', 'TWENTY', 'THIRTY', 'FORTY', 'FIFTY', 'SIXTY', 'SEVENTY', 'EIGHTY', 'NINETY'];

  const convert = (n) => {
    if (n < 20) return ones[n];
    if (n < 100) return tens[Math.floor(n / 10)] + (n % 10 ? ' ' + ones[n % 10] : '');
    if (n < 1000)
      return ones[Math.floor(n / 100)] + ' HUNDRED' + (n % 100 ? ' AND ' + convert(n % 100) : '');
    if (n < 100000)
      return convert(Math.floor(n / 1000)) + ' THOUSAND' + (n % 1000 ? ' ' + convert(n % 1000) : '');
    if (n < 10000000)
      return convert(Math.floor(n / 100000)) + ' LAKH' + (n % 100000 ? ' ' + convert(n % 100000) : '');
    return convert(Math.floor(n / 10000000)) + ' CRORE' + (n % 10000000 ? ' ' + convert(n % 10000000) : '');
  };

  return convert(Math.floor(num)) + ' RUPEES';
}

// ── School Logo Component with Reliable Fallback ──────────────
function SchoolLogo({ logoUrl }) {
  const [hasError, setHasError] = useState(false);

  if (logoUrl && !hasError) {
    return (
      <img
        crossOrigin="anonymous"
        src={logoUrl}
        alt="Logo"
        onError={() => setHasError(true)}
        style={{ width: '100%', height: '100%', objectFit: 'contain' }}
      />
    );
  }

  return (
    <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#000000" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d="M22 10v6M2 10l10-5 10 5-10 5z"/>
      <path d="M6 12v5c3 3 9 3 12 0v-5"/>
    </svg>
  );
}

// ── Helper: Format Receipt Currency Amount ──────────────────
function formatReceiptAmount(num) {
  const n = Number(num || 0);
  return n.toLocaleString('en-IN', {
    maximumFractionDigits: 2,
    minimumFractionDigits: n % 1 === 0 ? 0 : 2,
  });
}

// ── Single Receipt Slip Component ─────────────────────────────
function SingleReceiptSlip({
  schoolInfo,
  receiptNumber,
  sessionName,
  studentName,
  receiptDateFormatted,
  admissionNo,
  classRollStr,
  fatherName,
  paymentMode,
  items,
  totalFeeAmount,
  totalDiscount,
  totalFine,
  grandTotal,
  amountInWordsText,
  unpaidFeesList,
}) {
  return (
    <div
      style={{
        flex: 1,
        border: '1.5px solid #000000',
        padding: '12px 14px',
        backgroundColor: '#ffffff',
        color: '#000000',
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Arial, sans-serif',
        fontSize: '11px',
        lineHeight: 1.35,
        display: 'flex',
        flexDirection: 'column',
        boxSizing: 'border-box',
        minWidth: 0,
      }}
    >
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '12px', paddingBottom: '8px', borderBottom: '1px solid #000000' }}>
        <div
          style={{
            width: '46px',
            height: '46px',
            borderRadius: '50%',
            border: '1.5px solid #000000',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
            backgroundColor: '#ffffff',
            overflow: 'hidden',
          }}
        >
          <SchoolLogo logoUrl={schoolInfo?.logo} />
        </div>
        <div style={{ flex: 1, textAlign: 'center', paddingRight: '6px' }}>
          <div style={{ fontWeight: 800, fontSize: '13.5px', lineHeight: 1.2, textTransform: 'uppercase', color: '#000000' }}>
            {schoolInfo?.name || schoolInfo?.school_name || APP_NAME}
          </div>
          <div style={{ fontSize: '10.5px', color: '#222222', marginTop: '2px', fontWeight: 500 }}>
            {schoolInfo?.address || schoolInfo?.school_address || 'Patel Nagar, Garautha'}
          </div>
          <div style={{ fontSize: '10.5px', color: '#222222', marginTop: '1px', fontWeight: 500 }}>
            Contact {schoolInfo?.phone || schoolInfo?.school_phone || '93055 14258'}
          </div>
        </div>
      </div>

      {/* 4-Column Student / Receipt Info Table */}
      <table
        style={{
          width: '100%',
          borderCollapse: 'collapse',
          marginBottom: '12px',
          fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Arial, sans-serif',
          fontSize: '10.5px',
          tableLayout: 'fixed',
        }}
      >
        <colgroup>
          <col style={{ width: '22%' }} />
          <col style={{ width: '33%' }} />
          <col style={{ width: '20%' }} />
          <col style={{ width: '25%' }} />
        </colgroup>
        <tbody>
          <tr style={{ height: '26px' }}>
            <td style={{ border: '1px solid #000000', padding: '5px 6px', fontWeight: 700, verticalAlign: 'middle' }}>Rec Num</td>
            <td style={{ border: '1px solid #000000', padding: '5px 6px', verticalAlign: 'middle', wordBreak: 'break-all', fontSize: '10px', fontWeight: 600 }}>{receiptNumber}</td>
            <td style={{ border: '1px solid #000000', padding: '5px 6px', fontWeight: 700, verticalAlign: 'middle' }}>Session</td>
            <td style={{ border: '1px solid #000000', padding: '5px 6px', verticalAlign: 'middle', fontSize: '10px', fontWeight: 600 }}>{sessionName}</td>
          </tr>
          <tr style={{ height: '26px' }}>
            <td style={{ border: '1px solid #000000', padding: '5px 6px', fontWeight: 700, verticalAlign: 'middle' }}>Student</td>
            <td style={{ border: '1px solid #000000', padding: '5px 6px', fontWeight: 700, textTransform: 'uppercase', verticalAlign: 'middle', fontSize: '10px' }}>{studentName}</td>
            <td style={{ border: '1px solid #000000', padding: '5px 6px', fontWeight: 700, verticalAlign: 'middle' }}>Rec Date</td>
            <td style={{ border: '1px solid #000000', padding: '5px 6px', verticalAlign: 'middle', fontSize: '10px', fontWeight: 600 }}>{receiptDateFormatted}</td>
          </tr>
          <tr style={{ height: '26px' }}>
            <td style={{ border: '1px solid #000000', padding: '5px 6px', fontWeight: 700, verticalAlign: 'middle' }}>Admn Num</td>
            <td style={{ border: '1px solid #000000', padding: '5px 6px', verticalAlign: 'middle', wordBreak: 'break-all', fontSize: '10px', fontWeight: 600 }}>{admissionNo}</td>
            <td style={{ border: '1px solid #000000', padding: '5px 6px', fontWeight: 700, verticalAlign: 'middle' }}>Class/Roll</td>
            <td style={{ border: '1px solid #000000', padding: '5px 6px', verticalAlign: 'middle', fontSize: '10px', fontWeight: 600 }}>{classRollStr}</td>
          </tr>
          <tr style={{ height: '26px' }}>
            <td style={{ border: '1px solid #000000', padding: '5px 6px', fontWeight: 700, verticalAlign: 'middle' }}>Father</td>
            <td style={{ border: '1px solid #000000', padding: '5px 6px', textTransform: 'uppercase', verticalAlign: 'middle', fontSize: '10px', fontWeight: 600 }}>{fatherName}</td>
            <td style={{ border: '1px solid #000000', padding: '5px 6px', fontWeight: 700, verticalAlign: 'middle' }}>Mode</td>
            <td style={{ border: '1px solid #000000', padding: '5px 6px', fontWeight: 700, textTransform: 'uppercase', verticalAlign: 'middle', fontSize: '10px' }}>{paymentMode}</td>
          </tr>
        </tbody>
      </table>

      {/* Particulars Table with Column-wise Breakdown */}
      <table
        style={{
          width: '100%',
          borderCollapse: 'collapse',
          marginBottom: '12px',
          fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Arial, sans-serif',
          fontSize: '10px',
          tableLayout: 'fixed',
        }}
      >
        <colgroup>
          <col style={{ width: '38%' }} />
          <col style={{ width: '16%' }} />
          <col style={{ width: '15%' }} />
          <col style={{ width: '15%' }} />
          <col style={{ width: '16%' }} />
        </colgroup>
        <thead>
          <tr style={{ height: '26px' }}>
            <th style={{ border: '1px solid #000000', padding: '4px 5px', textAlign: 'left', fontWeight: 700, verticalAlign: 'middle', fontSize: '9.5px' }}>Description</th>
            <th style={{ border: '1px solid #000000', padding: '4px 5px', textAlign: 'right', fontWeight: 700, verticalAlign: 'middle', fontSize: '9.5px' }}>Fee Amt</th>
            <th style={{ border: '1px solid #000000', padding: '4px 5px', textAlign: 'right', fontWeight: 700, verticalAlign: 'middle', fontSize: '9.5px' }}>Discount</th>
            <th style={{ border: '1px solid #000000', padding: '4px 5px', textAlign: 'right', fontWeight: 700, verticalAlign: 'middle', fontSize: '9.5px' }}>Fine</th>
            <th style={{ border: '1px solid #000000', padding: '4px 5px', textAlign: 'right', fontWeight: 700, verticalAlign: 'middle', fontSize: '9.5px' }}>Total</th>
          </tr>
        </thead>
        <tbody>
          {items.map((it, idx) => (
            <tr key={idx} style={{ height: '24px' }}>
              <td style={{ border: '1px solid #000000', padding: '4px 5px', textTransform: 'uppercase', verticalAlign: 'middle', fontSize: '9.5px', fontWeight: 600, wordBreak: 'break-word' }}>
                {it.description}
              </td>
              <td style={{ border: '1px solid #000000', padding: '4px 5px', textAlign: 'right', verticalAlign: 'middle', fontSize: '9.5px', fontWeight: 600 }}>
                {formatReceiptAmount(it.feeAmount)}
              </td>
              <td style={{ border: '1px solid #000000', padding: '4px 5px', textAlign: 'right', verticalAlign: 'middle', fontSize: '9.5px', fontWeight: 600 }}>
                {it.discount > 0 ? `-${formatReceiptAmount(it.discount)}` : '0'}
              </td>
              <td style={{ border: '1px solid #000000', padding: '4px 5px', textAlign: 'right', verticalAlign: 'middle', fontSize: '9.5px', fontWeight: 600 }}>
                {it.fine > 0 ? `+${formatReceiptAmount(it.fine)}` : '0'}
              </td>
              <td style={{ border: '1px solid #000000', padding: '4px 5px', textAlign: 'right', verticalAlign: 'middle', fontSize: '9.5px', fontWeight: 700 }}>
                {formatReceiptAmount(it.amount)}
              </td>
            </tr>
          ))}
          <tr style={{ height: '26px' }}>
            <td style={{ border: '1px solid #000000', padding: '4px 5px', fontWeight: 700, verticalAlign: 'middle', fontSize: '9.5px' }}>Total</td>
            <td style={{ border: '1px solid #000000', padding: '4px 5px', textAlign: 'right', fontWeight: 700, verticalAlign: 'middle', fontSize: '9.5px' }}>{formatReceiptAmount(totalFeeAmount)}</td>
            <td style={{ border: '1px solid #000000', padding: '4px 5px', textAlign: 'right', fontWeight: 700, verticalAlign: 'middle', fontSize: '9.5px' }}>{totalDiscount > 0 ? `-${formatReceiptAmount(totalDiscount)}` : '0'}</td>
            <td style={{ border: '1px solid #000000', padding: '4px 5px', textAlign: 'right', fontWeight: 700, verticalAlign: 'middle', fontSize: '9.5px' }}>{totalFine > 0 ? `+${formatReceiptAmount(totalFine)}` : '0'}</td>
            <td style={{ border: '1px solid #000000', padding: '4px 5px', textAlign: 'right', fontWeight: 800, verticalAlign: 'middle', fontSize: '9.5px' }}>{formatReceiptAmount(grandTotal)}</td>
          </tr>
        </tbody>
      </table>

      {/* Authorised Signatory */}
      <div style={{ textAlign: 'right', marginTop: '16px', marginBottom: '10px', paddingRight: '4px' }}>
        <span style={{ fontSize: '10.5px', fontWeight: 700, fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Arial, sans-serif' }}>
          Authorised Signatory
        </span>
      </div>

      {/* Amount in Words */}
      <div style={{ marginBottom: '8px', fontSize: '9.5px', fontWeight: 700, fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Arial, sans-serif' }}>
        AMOUNT COLLECTED : INR {amountInWordsText} ONLY
      </div>

      {/* Unpaid Fees Table (if present) */}
      {unpaidFeesList && unpaidFeesList.length > 0 && (
        <div style={{ marginTop: 'auto', paddingTop: '8px' }}>
          <div style={{ fontSize: '9.5px', fontWeight: 700, marginBottom: '3px', fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Arial, sans-serif' }}>
            Unpaid fees:
          </div>
          <table
            style={{
              width: '100%',
              borderCollapse: 'collapse',
              fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Arial, sans-serif',
              fontSize: '9.5px',
              textAlign: 'center',
            }}
          >
            <thead>
              <tr style={{ height: '22px' }}>
                {unpaidFeesList.map((uf, idx) => (
                  <th key={idx} style={{ border: '1px solid #000000', padding: '3px 4px', fontWeight: 700, verticalAlign: 'middle' }}>
                    {uf.name}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              <tr style={{ height: '22px' }}>
                {unpaidFeesList.map((uf, idx) => (
                  <td key={idx} style={{ border: '1px solid #000000', padding: '3px 4px', verticalAlign: 'middle', fontWeight: 500 }}>
                    {Number(uf.amount || 0).toLocaleString()}
                  </td>
                ))}
              </tr>
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ── Main ReceiptModal ─────────────────────────────────────────
export function ReceiptModal({ open, onClose, receipt, schoolInfo }) {
  const receiptRef = useRef(null);
  const [isDownloading, setIsDownloading] = useState(false);

  if (!receipt) return null;

  // Extract student details
  const studentObj = receipt.student || {};
  const studentName =
    receipt.studentName ||
    receipt.student_name ||
    [studentObj.first_name, studentObj.middle_name, studentObj.last_name].filter(Boolean).join(' ') ||
    'Student';

  const admissionNo =
    receipt.admissionNo ||
    receipt.admission_no ||
    receipt.studentId ||
    receipt.student_id ||
    studentObj.admission_no ||
    studentObj.admission_number ||
    studentObj.id ||
    '-';

  const rawClass = (receipt.className || studentObj.class_name || studentObj.class?.class_name || studentObj.class?.name || '').replace(/^Class\s+/i, '');
  const sectionName = receipt.sectionName || studentObj.section_name || studentObj.section?.section_name || studentObj.section?.name || '';
  const rollNo = receipt.rollNo || receipt.roll_number || studentObj.roll_number || studentObj.roll_no || '';
  const classRollStr = [rawClass, sectionName, rollNo].filter(Boolean).join(' ') || '-';

  const fatherName =
    receipt.fatherName ||
    receipt.parent?.father_name ||
    studentObj.father_name ||
    studentObj.parent?.father_name ||
    studentObj.guardian_name ||
    '-';

  const sessionName =
    receipt.sessionName ||
    receipt.academic_session ||
    receipt.session ||
    studentObj.academic_session?.session_name ||
    '2026 - 2027';

  const receiptNumber =
    receipt.receiptNumber ||
    receipt.receipt_no ||
    (receipt.receipt_sequence ? String(receipt.receipt_sequence) : (receipt.id ? String(receipt.id).slice(0, 8) : '10001'));

  const receiptDateRaw = receipt.date || receipt.receipt_date || receipt.created_at || new Date();
  const receiptDateFormatted = dayjs(receiptDateRaw).format('DD/MM/YYYY');

  const paymentMode = String(receipt.paymentMethod || receipt.payment_mode || receipt.payments?.[0]?.payment_mode || 'CASH').toUpperCase();

  // Parse items
  const rawItems = receipt.itemsDetails || receipt.items || [];
  let items = rawItems.map((item) => {
    const description =
      item.installmentName ||
      item.installment_name ||
      item.installment?.installment_name ||
      item.fee_type_name ||
      item.feeTypeName ||
      item.fee_type?.name ||
      receipt.feeHeadName ||
      'Installment Fee';

    const discount = Number(item.discount_amount ?? item.discount ?? 0);
    const fine = Number(item.fine_amount ?? item.fine ?? 0);
    const paid = Number(item.paying ?? item.paid_amount ?? item.amount_paid ?? item.amount ?? 0);

    let feeAmount = Number(
      item.feeAmount ??
      item.original_amount ??
      item.base_amount ??
      item.balance_amount ??
      item.installment?.original_amount ??
      item.installment?.amount ??
      (paid ? paid + discount - fine : 0)
    );

    if (!feeAmount && paid) {
      feeAmount = paid;
    }

    const amount = paid || Math.max(0, feeAmount - discount + fine);

    return {
      description,
      feeAmount,
      discount,
      fine,
      amount,
    };
  });

  const grandTotal = Number(
    receipt.amount ??
    receipt.paid_amount ??
    receipt.total_amount ??
    items.reduce((sum, i) => sum + i.amount, 0)
  );

  if (items.length === 0) {
    items = [{
      description: receipt.feeHeadName || 'INSTALLMENT FEE 1',
      feeAmount: grandTotal,
      discount: Number(receipt.discount_amount ?? receipt.total_discount ?? 0),
      fine: Number(receipt.fine_amount ?? receipt.total_fine ?? 0),
      amount: grandTotal,
    }];
  }

  const totalFeeAmount = items.reduce((sum, it) => sum + Number(it.feeAmount || 0), 0);
  const totalDiscount = Number(receipt.discount_amount ?? receipt.total_discount ?? items.reduce((sum, it) => sum + Number(it.discount || 0), 0));
  const totalFine = Number(receipt.fine_amount ?? receipt.total_fine ?? items.reduce((sum, it) => sum + Number(it.fine || 0), 0));

  const amountInWordsText = numberToWordsINR(grandTotal);

  // Unpaid fees list if available in receipt data
  let unpaidFeesList = null;
  if (Array.isArray(receipt.unpaidFees) && receipt.unpaidFees.length > 0) {
    unpaidFeesList = receipt.unpaidFees;
  } else if (Array.isArray(receipt.unpaid_installments) && receipt.unpaid_installments.length > 0) {
    unpaidFeesList = receipt.unpaid_installments.map((u) => ({
      name: u.installment_name || u.name || u.month || 'Term',
      amount: Number(u.balance_amount || u.amount || 0),
    }));
  }

  const receiptProps = {
    schoolInfo,
    receiptNumber,
    sessionName,
    studentName,
    receiptDateFormatted,
    admissionNo,
    classRollStr,
    fatherName,
    paymentMode,
    items,
    totalFeeAmount,
    totalDiscount,
    totalFine,
    grandTotal,
    amountInWordsText,
    unpaidFeesList,
  };

  // ── Download PDF (2-on-1 A4 Page) ──────────────────────────
  const handleDownloadPDF = async () => {
    setIsDownloading(true);
    const toastId = toast.loading('Generating PDF receipt...');
    try {
      const element = receiptRef.current;
      if (!element) throw new Error('Receipt content not loaded');

      // Create an isolated clone visible in viewport for hardware-accelerated text rendering
      const clone = element.cloneNode(true);
      clone.style.position = 'fixed';
      clone.style.left = '0px';
      clone.style.top = '0px';
      clone.style.zIndex = '-9999';
      clone.style.opacity = '1';
      clone.style.width = '820px';
      clone.style.transform = 'none';
      clone.style.boxShadow = 'none';
      clone.style.margin = '0';
      clone.style.padding = '12px';
      clone.style.backgroundColor = '#ffffff';
      document.body.appendChild(clone);

      const canvas = await html2canvas(clone, {
        scale: 3,
        useCORS: true,
        allowTaint: true,
        logging: false,
        backgroundColor: '#ffffff',
        width: 820,
        windowWidth: 820,
      });

      document.body.removeChild(clone);

      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF('p', 'mm', 'a4');

      const pdfWidth = pdf.internal.pageSize.getWidth();
      const margin = 8;
      const printWidth = pdfWidth - (margin * 2);
      const printHeight = (canvas.height * printWidth) / canvas.width;

      pdf.addImage(imgData, 'PNG', margin, margin, printWidth, printHeight);
      pdf.save(`Receipt_${receiptNumber}.pdf`);

      toast.success('Receipt downloaded successfully!', { id: toastId });
    } catch (error) {
      console.error('Error generating PDF:', error);
      toast.error('Failed to generate PDF', { id: toastId });
    } finally {
      setIsDownloading(false);
    }
  };

  // ── Print (2-on-1 A4 Page via fast isolated iframe) ────────
  const handlePrint = () => {
    const printContent = receiptRef.current;
    if (!printContent) return;

    let iframe = document.getElementById('receipt-print-frame');
    if (!iframe) {
      iframe = document.createElement('iframe');
      iframe.id = 'receipt-print-frame';
      iframe.style.position = 'fixed';
      iframe.style.right = '0';
      iframe.style.bottom = '0';
      iframe.style.width = '0';
      iframe.style.height = '0';
      iframe.style.border = '0';
      iframe.style.visibility = 'hidden';
      document.body.appendChild(iframe);
    }

    const doc = iframe.contentWindow.document;
    doc.open();
    doc.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <title>Fee Receipt - ${receiptNumber}</title>
          <style>
            @page {
              size: A4 portrait;
              margin: 6mm;
            }
            * {
              box-sizing: border-box;
              margin: 0;
              padding: 0;
            }
            body {
              font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Arial, sans-serif;
              background-color: #ffffff;
              color: #000000;
              -webkit-print-color-adjust: exact;
              print-color-adjust: exact;
              padding: 2mm;
            }
            .print-container {
              display: flex;
              flex-direction: row;
              gap: 12px;
              width: 100%;
            }
          </style>
        </head>
        <body>
          <div class="print-container">
            ${printContent.innerHTML}
          </div>
        </body>
      </html>
    `);
    doc.close();

    setTimeout(() => {
      iframe.contentWindow.focus();
      iframe.contentWindow.print();
    }, 150);
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      slotProps={{ paper: { sx: { borderRadius: 3, overflow: 'hidden' } } }}
    >
      <DialogTitle
        component="div"
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          p: 2,
          bgcolor: tokens.colors.background.paper,
          borderBottom: `1px solid ${tokens.colors.border}`,
        }}
      >
        <Typography variant="h6" fontWeight={700} color={tokens.colors.text.primary}>
          Fee Receipt
        </Typography>
        <IconButton
          onClick={onClose}
          size="small"
          sx={{
            bgcolor: `${tokens.colors.primary.main}15`,
            '&:hover': { bgcolor: `${tokens.colors.primary.main}25` },
          }}
        >
          <X size={20} color={tokens.colors.primary.main} />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: 2, bgcolor: '#f1f5f9', display: 'flex', justifyContent: 'center' }}>
        {/* Dual Receipt Container (Rendered Side-by-Side) */}
        <div
          ref={receiptRef}
          id="dual-receipt-container"
          style={{
            width: '100%',
            maxWidth: '820px',
            backgroundColor: '#ffffff',
            padding: '12px',
            display: 'flex',
            flexDirection: 'row',
            gap: '12px',
            boxSizing: 'border-box',
            borderRadius: '2px',
            boxShadow: '0 4px 16px rgba(0,0,0,0.06)',
            fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Arial, sans-serif',
          }}
        >
          <SingleReceiptSlip {...receiptProps} />
          <SingleReceiptSlip {...receiptProps} />
        </div>
      </DialogContent>

      <DialogActions
        sx={{
          p: 2,
          px: 3,
          bgcolor: tokens.colors.background.paper,
          borderTop: `1px solid ${tokens.colors.border}`,
          display: 'flex',
          justifyContent: 'space-between',
        }}
      >
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button variant="outlined" size="small" startIcon={<MessageSquare size={16} />} sx={{ borderRadius: 2 }} disabled>
            WhatsApp
          </Button>
          <Button variant="outlined" size="small" startIcon={<Mail size={16} />} sx={{ borderRadius: 2 }} disabled>
            Email
          </Button>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant="outlined"
            startIcon={<Printer size={18} />}
            onClick={handlePrint}
            sx={{ borderRadius: 2, fontWeight: 600 }}
          >
            Print
          </Button>
          <Button
            variant="contained"
            startIcon={<Download size={18} />}
            onClick={handleDownloadPDF}
            disabled={isDownloading}
            sx={{
              borderRadius: 2,
              fontWeight: 600,
              boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
              '&:hover': { boxShadow: '0 6px 12px rgba(0,0,0,0.15)' },
            }}
          >
            {isDownloading ? 'Generating...' : 'Download PDF'}
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
}
